import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, Linkedin, ArrowRight, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvisorsPage() {
  const navigate = useNavigate();
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        const response = await axios.get(`${API}/advisors`);
        setAdvisors(response.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchAdvisors();
  }, []);

  const handleBookSession = () => {
    // Check if startup is logged in
    const token = sessionStorage.getItem('startup_token');
    if (token) {
      navigate('/advisors/book');
    } else {
      navigate('/advisors/login');
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-[#075FA3] relative overflow-hidden" data-testid="advisors-hero">
        <div className="absolute inset-0 subtle-gradient opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[#4DBDDE] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              Mentorship
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold text-white mb-6 font-['Bricolage_Grotesque']"
              data-testid="advisors-heading"
            >
              Book a Meeting with an Advisor
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-white/90 leading-relaxed mb-8"
            >
              Whether you&apos;re exploring an idea, building an MVP, or looking for funding guidance — 
              our advisors are here to help. Book a one-on-one session with experienced founders, 
              investors, and startup operators from the CfE and Catalyst network.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Button 
                onClick={handleBookSession}
                className="bg-white text-[#075FA3] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                data-testid="book-session-cta"
              >
                <Lock className="w-5 h-5 mr-2" />
                Login to Book a Session
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-6 bg-[#C83041]" data-testid="info-banner">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
            <Lock className="w-6 h-6 text-white" />
            <p className="text-white font-medium">
              Only registered startups can book advisor sessions. 
              <Link to="/register" className="underline ml-2 hover:text-white/80">
                Register your startup first
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Advisors Grid */}
      <section className="section-padding bg-white" data-testid="advisors-grid">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#231F20] mb-4 font-['Bricolage_Grotesque']">
              Our Advisors
            </h2>
            <p className="text-[#58595B] max-w-2xl mx-auto">
              Meet the experienced mentors ready to guide your entrepreneurial journey
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-2xl h-80"></div>
                </div>
              ))}
            </div>
          ) : advisors.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {advisors.map((advisor, index) => (
                <motion.div
                  key={advisor.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className="h-full border border-gray-200 hover:border-[#075FA3]/50 hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    data-testid={`advisor-card-${advisor.id}`}
                  >
                    <CardContent className="p-0">
                      {/* Photo */}
                      <div className="h-56 bg-gradient-to-br from-[#075FA3]/10 to-[#4DBDDE]/10 overflow-hidden">
                        {advisor.photo_url ? (
                          <img 
                            src={advisor.photo_url} 
                            alt={advisor.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-24 h-24 rounded-full bg-[#075FA3] flex items-center justify-center text-white text-4xl font-bold font-['Bricolage_Grotesque']">
                              {advisor.name.charAt(0)}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-[#231F20] font-['Bricolage_Grotesque']">
                            {advisor.name}
                          </h3>
                          {advisor.linkedin_url && (
                            <a 
                              href={advisor.linkedin_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#075FA3] hover:text-[#0077B6]"
                            >
                              <Linkedin className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                        <p className="text-[#C83041] text-sm font-medium mb-2">
                          {advisor.expertise}
                        </p>
                        <p className="text-sm text-[#58595B] mb-1">
                          {advisor.title}
                        </p>
                        <p className="text-sm text-[#58595B] line-clamp-2">
                          {advisor.bio}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#F3F4F6] rounded-2xl p-12 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#075FA3]/10 flex items-center justify-center mx-auto mb-6">
                <User className="w-10 h-10 text-[#075FA3]" />
              </div>
              <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                Advisors Coming Soon
              </h3>
              <p className="text-[#58595B] max-w-md mx-auto">
                We&apos;re onboarding experienced advisors from our network of founders, investors, 
                and startup operators. Check back soon to book your session.
              </p>
            </motion.div>
          )}

          {/* CTA to Login */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Button 
              onClick={handleBookSession}
              className="bg-[#C83041] hover:bg-[#A92332] text-white rounded-full px-10 py-6 text-lg font-semibold"
              data-testid="book-session-bottom-cta"
            >
              <Lock className="w-5 h-5 mr-2" />
              Login to Book a Session
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
